/**
 * UTM attribution — captures utm_source / utm_medium / utm_campaign / utm_content / utm_term
 * on first page load and persists them in localStorage for 30 days. When the
 * user signs up or signs in for the first time, the AuthContext attaches the
 * UTM payload to the user record (POST /api/users/utm) so we can attribute
 * every conversion back to its acquisition channel — viral shares, marketing
 * campaigns, partner referrals, the lot.
 *
 * Also captures referrer and landing-page URL for first-visit context.
 *
 * Privacy: nothing here is PII. The data is attached to the user only at
 * sign-up / sign-in; before that it lives only in this device's localStorage.
 */

const STORAGE_KEY = 'hibff_attribution_v1';
const VALIDITY_DAYS = 30;

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

function _now() { return Date.now(); }

/**
 * Read existing attribution if it's still within the validity window.
 */
export function getAttribution() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.captured_at) return null;
    const ageDays = (_now() - parsed.captured_at) / (1000 * 60 * 60 * 24);
    if (ageDays > VALIDITY_DAYS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
}

/**
 * If the URL has any utm_* parameter and we have not captured one yet (or the
 * existing capture is expired), persist the new attribution. First-touch
 * attribution wins — we don't overwrite if a previous valid capture exists,
 * because the first source that brought the user is usually the most
 * meaningful for marketing analytics.
 */
export function captureAttributionFromUrl() {
  if (typeof window === 'undefined') return null;
  try {
    const search = new URLSearchParams(window.location.search);
    const utm = {};
    for (const k of UTM_KEYS) {
      const v = search.get(k);
      if (v) utm[k] = v.slice(0, 200);
    }
    // No UTM params? Nothing to do — preserve whatever was there before.
    if (Object.keys(utm).length === 0) return getAttribution();

    // First-touch wins: don't overwrite an existing valid capture.
    const existing = getAttribution();
    if (existing && existing.utm_source) return existing;

    const payload = {
      ...utm,
      referrer: (document.referrer || '').slice(0, 500) || null,
      landing_url: window.location.href.slice(0, 500),
      captured_at: _now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    return payload;
  } catch (_) {
    return null;
  }
}

/**
 * Build a URL-encoded share URL with utm_* params injected.
 *
 * Example:
 *   buildShareUrl({ path: '/conversations/practice/maya', source: 'whatsapp', campaign: 'practice-share' })
 *   → "https://hibff.com/conversations/practice/maya?utm_source=whatsapp&utm_medium=share&utm_campaign=practice-share"
 */
export function buildShareUrl({ path, source = 'share', medium = 'share', campaign = 'organic-share', content }) {
  const origin = (typeof window !== 'undefined' && window.location?.origin) || 'https://hibff.com';
  // For shares we always want them landing on the production domain, not the
  // staging preview URL — sharing a *.preview.emergentagent.com URL is a bug.
  // Detect preview by hostname and override to hibff.com.
  let base = origin;
  try {
    const u = new URL(origin);
    if (u.hostname.endsWith('.preview.emergentagent.com')) base = 'https://hibff.com';
  } catch (e) {
    // Malformed origin — fall back to the raw value rather than crash render.
    void e;
  }

  const url = new URL(path.startsWith('/') ? path : `/${path}`, base);
  url.searchParams.set('utm_source', source);
  url.searchParams.set('utm_medium', medium);
  url.searchParams.set('utm_campaign', campaign);
  if (content) url.searchParams.set('utm_content', content);
  return url.toString();
}

/**
 * Mark the current session's attribution as "consumed" (the user signed up).
 * Keeps the data in localStorage for analytics dashboards but flags that
 * it's been attached to a user record so we don't re-attach on every page.
 */
export function markAttributionConsumed() {
  try {
    const existing = getAttribution();
    if (!existing) return;
    existing.consumed = true;
    existing.consumed_at = _now();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
  } catch (e) {
    // localStorage write may fail in private browsing or quota-exceeded cases.
    void e;
  }
}

/**
 * Returns the payload to send to /api/users/utm when a user signs up / signs in.
 * Returns null if there's nothing worth sending.
 */
export function getAttributionForServer() {
  const a = getAttribution();
  if (!a || a.consumed) return null;
  return {
    utm_source: a.utm_source || null,
    utm_medium: a.utm_medium || null,
    utm_campaign: a.utm_campaign || null,
    utm_content: a.utm_content || null,
    utm_term: a.utm_term || null,
    referrer: a.referrer || null,
    landing_url: a.landing_url || null,
    captured_at_iso: new Date(a.captured_at).toISOString(),
  };
}
